import { Box, InputBase, useTheme, ButtonBase } from "@mui/material"
import { useState, useEffect, useRef } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { useAddInboundLinesMutation } from "../../../newapi/inbound/inboundsSlice";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import { useGetProductsSelectQuery } from '../../../newapi/warehouse/productSlice';
import ShValidatedInputDropdown from '../../theme/dropdowns/ShValidatedInputDropdown';
import NumberInput from '../../theme/inputs/NumberInput';
import { DropdownBaseV2 } from '../../theme/dropdowns/Dropdown';
import { ReactComponent as BarcodeSvg } from "../../../styles/svg/barcode.svg";

const MAddInboundLine = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const inputRef = useRef(null);

    // Only the products of the inbound's supplier; without a supplier every product of the relation.
    const { data: products } = useGetProductsSelectQuery({relationId: props.relationId, supplierId: props.supplierId ?? null},
        { skip: !props.relationId }
    );

    const [productOptions, setProductOptions] = useState([]);

    useEffect(() => {
        if (products) {
            const mappedProducts = products.map(product => ({
                id: product.id,
                name: product.combined,
                barcodes:product.barcodes,
                barcodeArray: product.barcodes?.split(", "),
            }));

            setProductOptions(mappedProducts);
        }
    }, [products]);

    const [newInboundLines, setNewInboundLines] = useState([]);

    const addProductLine = (product) => {
        setNewInboundLines(prev => {
            const existing = prev.findIndex(line => line.productId === product.id);
            if (existing > -1) {
                const updated = [...prev];
                updated[existing] = {
                    ...updated[existing],
                    amountForecasted: updated[existing].amountForecasted + 1,
                };
                return updated;
            }
            return [...prev, {
                productId: product.id,
                productName: product.name || product.combined,
                barcodes: product.barcodes,
                amountForecasted: 1,
            }];
        });
    };

    const handleProductSelect = (key, val) => {
        const lineIndex = parseInt(key.split(".")[0]);
        const product = productOptions.find(p => p.id === val);
        if (product) {
            const updated = [...newInboundLines];
            updated[lineIndex] = {
                ...updated[lineIndex],
                productId: product.id,
                productName: product.name,
                barcodes: product.barcodes,
            };
            setNewInboundLines(updated);
        }
    };

    const handleAmountChange = (index, value) => {
        const updated = [...newInboundLines];
        updated[index] = {
            ...updated[index],
            amountForecasted: value,
        };
        setNewInboundLines(updated);
    };

    const handleDeleteLineRow = (index) => {
        setNewInboundLines(prev => prev.filter((_, i) => i !== index));
    };

    const handleClose = () => {
        setNewInboundLines([]);
        props.handleClose();
    }

    const [addInboundLines, {isLoading}] = useAddInboundLinesMutation();
    const SaveInboundLines = async () => {
        try {
            const filteredLines = newInboundLines
            .filter(x => x.productId > 0 && x.amountForecasted > 0)
            .map(x => ({
                productId: x.productId,
                amountForecasted: x.amountForecasted
            }));

            const requestData = {
                inboundId: props.inboundId,
                body: {
                    inboundlines: filteredLines
                }
            }

            const data = await addInboundLines(requestData).unwrap()
            showToastMessagePostRequest(t, data);
            handleClose();
        } catch (err) {
            // Handle other errors, such as network errors
            showToastMessageRequestError(t("create_failed"), t, err);
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return(
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign:"center",
                    paddingY:3,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                }}>
                <Subheading>{t("add_inboundline")}</Subheading>
            </Box>
            <Box
                sx={{
                    maxWidth:600,
                    minWidth:500,
                    pt:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    overflow:"hidden",
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    px:3,
                }}
            >
                {/* Scan / search bar */}
                <Box
                    sx={{
                        display:"flex",
                        gap:1.5,
                        height:40,
                        width:1,
                        background:colors.grey[100],
                        borderRadius:5,
                        p:0.5,
                    }}
                >
                    <Box
                        sx={{
                            height:32,
                            px:2,
                            borderRadius:4,
                            background:colors.grey[200],
                            "& .echeck-barcode-svg" : {
                                width:32,
                                height:32,
                                "& path" : {
                                    fill: colors.txt["secondary"]
                                }
                            }
                        }}
                    >
                        <BarcodeSvg
                            className="echeck-barcode-svg"
                        />
                    </Box>
                    <InputBase
                        inputRef={inputRef}
                        autoFocus
                        placeholder={t("scan_product")}
                        sx={{
                            height:32,
                            flex:1,
                            color:colors.txt["primary"]
                        }}
                        onKeyDown={(e) => {
                            if(e.key === "Enter") {
                                const product = productOptions?.find(p => p.barcodeArray?.includes(e.target.value));
                                if(product) {
                                    addProductLine(product);
                                    e.target.value = "";
                                }
                            }
                        }}
                    />
                    <DropdownBaseV2
                        name={"product"}
                        onChange={(selected) => {
                            let product = productOptions[selected];
                            if(product) {
                                addProductLine(product);
                            }
                            if(inputRef.current) {
                                inputRef.current.focus();
                            }
                        }}
                        displayName={"name"}
                        options={productOptions}
                        closeOnSelection
                        resetOnSelection
                        multiple={false}
                        selected={-1}
                        searchOptions={{ enabled: true, placeholder: t("search_product_placeholder"), searchables: ["barcodes", "name"] }}
                    >
                        {({anchorEl,onClick}) => (
                            <ButtonBase
                                onClick={onClick}
                                sx={{
                                    backgroundColor : `${theme.palette.mode === "dark" ?colors.txt["secondary"] : colors.grey[400]}`,
                                    borderRadius:4,
                                    display:"flex",
                                    height:32,
                                    px:2,
                                    alignItems:'center',
                                    justifyContent:'center',
                                    fontFamily:"Roboto, sans-serif",
                                    fontWeight:500,
                                    fontSize:14,
                                    color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"]
                                }}
                            >
                                {t("choose")}
                            </ButtonBase>
                        )}
                    </DropdownBaseV2>
                </Box>

                {/* Inbound line rows */}
                {newInboundLines.map((inboundLine, index) => (
                    <Box key={"new-inbound-line-"+index}
                        sx={{
                            width:1,
                            borderRadius:4,
                            color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400],
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            boxShadow:theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                            p:2,
                        }}
                    >
                        <Box
                            sx={{
                                display:'flex',
                                justifyContent:'space-between',
                                alignItems:'center',
                            }}
                        >
                            <Box
                                sx={{
                                    flex:1,
                                    overflow:"hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        flex:1,
                                        backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[400] : colors.grey[100]}`,
                                        borderRadius:4,
                                        display:"flex",
                                        height:32,
                                        overflow:"hidden"
                                    }}
                                >
                                    <ShValidatedInputDropdown
                                        height={32}
                                        tabIndex={-1}
                                        key={"new-inboundLine-product-"+index}
                                        displayName={"name"}
                                        changeField={"id"}
                                        valueKey={"id"}
                                        closeOnSelection
                                        searchOptions={{
                                            enabled: true,
                                            placeholder: t("search_product_placeholder"),
                                            searchables: ["barcodes", "id"]
                                        }}
                                        disallowDeselection
                                        name={`${index}.productId`}
                                        selected={productOptions?.find(item => item.id === inboundLine.productId)?.id ?? -1}
                                        options={productOptions ? productOptions : []}
                                        noSelection={t("choose_option")}
                                        onChange={handleProductSelect}
                                    />
                                </Box>
                            </Box>

                            <ButtonBase
                                tabIndex={-1}
                                onClick={() => handleDeleteLineRow(index)}
                                sx={{
                                    height:32,
                                    width:32,
                                    ml:1.5,
                                    borderRadius:5,
                                    color:colors.red[400],
                                    background:colors.red[100],
                                }}
                            >
                                <DeleteOutlinedIcon fontSize='small'/>
                            </ButtonBase>
                        </Box>

                        {/* Amount bar */}
                        <Box
                            sx={{
                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                height:40,
                                borderRadius:5,
                                display:"flex",
                                alignItems:"center",
                                pl:0.5,
                                pr:0.5,
                                mt:2,
                                gap:1,
                            }}
                        >
                            <Box
                                sx={{
                                    background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow:theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                    height:32,
                                    borderRadius:4,
                                    lineHeight:"32px",
                                    px:2,
                                }}
                            >
                                <Text>{t("quantity")}</Text>
                            </Box>
                            <Box sx={{ flex:1 }}>
                                <NumberInput 
                                    value={inboundLine.amountForecasted}
                                    change={(e) => handleAmountChange(index, e)}
                                />
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
            <Box
                component="footer"
                sx={{
                    pt:2,
                    display:"flex",
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems:"center",
                    justifyContent:"space-between",
                    paddingRight:3,
                    paddingLeft:3,
                    zIndex:1058,
                    position:"relative",
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton className={"group-btn"} color={"green"}
                    variant="contained"
                    isLoading={isLoading}
                    onClick={() => SaveInboundLines()}
                    >{t("add")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MAddInboundLine;
